import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckCircle, 
  Check,
  Code, 
  Key, 
  Database, 
  Search, 
  Network, 
  Shield,
  Copy,
  Play,
  ArrowRight,
  Sparkles,
  Download
} from "lucide-react";
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import CodeBlock from '@/components/ui/code-block';
import { toast } from '@/components/ui/sonner';
import PageLayout from '@/components/layout/PageLayout';
import CreateKeyModal from '@/components/api-keys/CreateKeyModal';
import { apiKeyService } from '@/services/apiKeyService';
import { exportDeleteService } from '@/services/exportDeleteService';
import { teamService } from '@/services/teamService';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  isComplete: boolean;
}

const GettingStarted = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState<'python'>('python');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [openAccordionItem, setOpenAccordionItem] = useState<string>('install');
  const [formData, setFormData] = useState({
    apiKey: '',
    role: 'writer',
    userId: 'user_123',
    projectId: 'project_abc',
    content: 'The user prefers dark mode and weekly summary emails.',
    tags: 'preferences,notifications',
    consentFlag: true,
    query: 'What does this user prefer for notifications?',
    scope: 'user',
    summarized: true,
    type: 'facts',
    similarityThreshold: [0.8],
    resultLimit: [10],
    exportType: 'fact',
    exportFormat: 'json',
    deleteScope: 'user'
  });

  const steps: OnboardingStep[] = [
    {
      id: 'install',
      title: 'Install SDK',
      description: 'Choose your preferred environment and install the SDK',
      isComplete: completedSteps.includes('install')
    },
    {
      id: 'api-key',
      title: 'Generate API Key',
      description: 'Use your API token to authenticate. You\'ll need a key with Writer or Manager permissions',
      isComplete: completedSteps.includes('api-key')
    },
    {
      id: 'write-memory',
      title: 'Write a Memory',
      description: 'Populate your first scoped memory with tags and consent flag',
      isComplete: completedSteps.includes('write-memory')
    },
    {
      id: 'recall-memory',
      title: 'Recall Memory',
      description: 'Query your memory using semantic search, TTL-aware scopes, and summarization toggle',
      isComplete: completedSteps.includes('recall-memory')
    },
    {
      id: 'compliance',
      title: 'Compliance (Delete or Export)',
      description: 'Export or delete memory by user, project, or tag for GDPR compliance',
      isComplete: completedSteps.includes('compliance')
    }
  ];

  // Helper function to get next step to open
  const getNextStep = (completed: string[]) => {
    const stepOrder = ['install', 'api-key', 'write-memory', 'recall-memory', 'compliance'];
    for (const step of stepOrder) {
      if (!completed.includes(step)) return step;
    }
    return '';
  };

  // Save completion state to localStorage with error handling
  // useEffect(() => {
  //   try {
  //     teamService.updateOnboardingStep(completedSteps);
  //     //localStorage.setItem('onboarding-completed-steps', JSON.stringify(completedSteps));
  //   } catch (error) {
  //     console.warn('Failed to save onboarding progress to localStorage:', error);
  //   }
  // }, [completedSteps]);

  // Load user profile to determine initial accordion item and completed steps
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const profileData = await teamService.getUserProfile();
        const saved = profileData.onboardingStep;
        const steps = saved ? JSON.parse(saved) : [];
        
        // Set completed steps from profile
        setCompletedSteps(steps);
        
        if (steps.length === 0) {
          setOpenAccordionItem('install');
        } else {
          const nextStep = steps.length < 5 ? getNextStep(steps) : '';
          setOpenAccordionItem(nextStep);
        }
      } catch (error) {
        console.warn('Failed to load onboarding steps from profile:', error);
        setOpenAccordionItem('install');
        setCompletedSteps([]);
      }
    };
    
    loadUserProfile();
  }, []);

  // Check if all steps are complete
  const allStepsComplete = steps.every(step => step.isComplete);
  const progressPercentage = (completedSteps.length / steps.length) * 100;

  const markStepComplete = (stepId: string) => {
    if (!completedSteps.includes(stepId)) {
      const newCompletedSteps = [...completedSteps, stepId];
      setCompletedSteps(newCompletedSteps);
      
      try {
        //localStorage.setItem('onboarding-completed-steps', JSON.stringify(newCompletedSteps));
        teamService.updateOnboardingStep(newCompletedSteps);
      } catch (error) {
        console.warn('Failed to save step completion to localStorage:', error);
      }
      
      toast(`Step "${steps.find(s => s.id === stepId)?.title}" completed! 🎉`);
      
      // Auto-open next step
      const nextStep = getNextStep(newCompletedSteps);
      if (nextStep) {
        setOpenAccordionItem(nextStep);
      }
    }
  };

  const getCodeSnippet = (type: string, language: string) => {
    const snippets = {
      install: {
        python: "pip install recallio",
        typescript: "npm install recallio"
      },
      auth: {
        python: `from recallio import (
    RecallioClient,
    MemoryWriteRequest,
    MemoryRecallRequest,
    RecallSummaryRequest,
    MemoryExportRequest,
    MemoryDeleteRequest
)

client = RecallioClient(api_key="${formData.apiKey || 'your-api-key'}")`,
        typescript: `import { RecallioClient } from 'recallio';
const client = new RecallioClient({ apiKey: "${formData.apiKey || 'your-api-key'}" });`
      },
      write: {
        python: `req = MemoryWriteRequest(
  userId="${formData.userId}",
  projectId="${formData.projectId}",
  content="${formData.content}",
  tags=[${formData.tags.split(',').map(tag => `"${tag.trim()}"`).join(', ')}],
  consentFlag=${formData.consentFlag}
})
client.memory.write(req)`,
        typescript: `await client.writeMemory({
  userId:"${formData.userId}",
  projectId:"${formData.projectId}",
  content:"${formData.content}",
  tags:[${formData.tags.split(',').map(tag => `"${tag.trim()}"`).join(', ')}],
  consentFlag:${formData.consentFlag}
})`
      },
      recall: {
        python: `recall_req = MemoryRecallRequest(
  userId="${formData.userId}",
  projectId="${formData.projectId}",
  query="${formData.query}",
  scope="${formData.scope}",
  summarized=${formData.summarized},
  type="${formData.type}",
  similarityThreshold=${formData.similarityThreshold[0]},
  limit=${formData.resultLimit[0]}
)
results = client.recall_memory(recall_req)
for m in results:
    print(m.content, m.similarityScore)`,
        typescript: `const result = await client.recallMemory({
  userId:"${formData.userId}",
  projectId:"${formData.projectId}",
  query:"${formData.query}",
  scope:"${formData.scope}",
  summarized:${formData.summarized},
  type:"${formData.type}",
  similarityThreshold:${formData.similarityThreshold[0]},
  limit:${formData.resultLimit[0]}
});
console.log(result);`
      },
      graph: {
        python: `client.graph.get_relationships(
  user_id="${formData.userId}",
  project_id="${formData.projectId}"
)`
      },
      compliance: {
        python: `# Export
export_req = MemoryExportRequest(
  type="${formData.exportType}",
  format="${formData.exportFormat}",
  user_id="${formData.userId}"
)
data = client.export_memory(export_req)
print(data)

# Delete
delete_req = MemoryDeleteRequest(
  scope="${formData.deleteScope}",
  user_id="${formData.userId}"
)
client.delete_memory(delete_req)`,
        typescript: `// Export
const data = await client.exportMemory({
  type:"${formData.exportType}",
  scope:"${formData.deleteScope}",
  userId:"${formData.userId}"
)
console.log(data);

// Delete
await client.deleteMemory({
  scope:"${formData.deleteScope}",
  userId:"${formData.userId}"
})
`
      }
    };

    return snippets[type as keyof typeof snippets]?.[language as keyof typeof snippets] || '';
  };

  const handleLaunchPlayground = () => {
    // Mark onboarding as complete
    localStorage.setItem('onboarding-marked-complete', 'true');
    toast("Welcome to Recallio! 🚀", {
      description: "You've completed the onboarding. Let's explore the dashboard!"
    });
    navigate('/dashboard');
  };

  // Check if user is seeing congratulations for the first time
  const congratulationsSeen = localStorage.getItem('onboarding-congratulations-shown');
  const showCongratulations = allStepsComplete && congratulationsSeen !== 'true';

  // If all steps are complete and user hasn't seen congratulations yet, show completion view
  if (showCongratulations) {
    // Mark congratulations as shown
    localStorage.setItem('onboarding-congratulations-shown', 'true');
    
    return (
      <PageLayout 
        title="Onboarding Complete!" 
        description="You've successfully completed the Recallio onboarding"
      >
        <div className="flex items-center justify-center">
          <Card className="max-w-2xl w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary-foreground" />
              </div>
              <CardTitle className="text-3xl font-bold">Congratulations! 🎉</CardTitle>
              <CardDescription className="text-lg">
                You've successfully completed the Recallio onboarding
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center space-y-4">
                <p className="text-muted-foreground">
                  You're now ready to build with Recallio's compliance-first memory API.
                  Start exploring your dashboard to manage your memory infrastructure.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <Card className="text-center p-4">
                    <CardContent className="p-0">
                      <div className="text-2xl font-bold text-primary">5</div>
                      <div className="text-sm text-muted-foreground">Steps Completed</div>
                    </CardContent>
                  </Card>
                  <Card className="text-center p-4">
                    <CardContent className="p-0">
                      <div className="text-2xl font-bold text-primary">100%</div>
                      <div className="text-sm text-muted-foreground">Setup Progress</div>
                    </CardContent>
                  </Card>
                  <Card className="text-center p-4">
                    <CardContent className="p-0">
                      <div className="text-2xl font-bold text-primary">Ready</div>
                      <div className="text-sm text-muted-foreground">To Build</div>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={handleLaunchPlayground} size="lg" className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4" />
                  Launch Recallio Dashboard
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/api-docs" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    View API Docs
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  const handleCreateKey = async (
    name: string, 
    scopes: string[], 
    environment: string, 
    expiresAt: string | null,
    role: string,
    ttlDays: number
  ) => {
    try {
      const newKey = await apiKeyService.createApiKey(
        name,
        scopes,
        environment,
        expiresAt,
        role,
        ttlDays
      );

      // Update form data with the new API key
      setFormData(prev => ({...prev, apiKey: newKey.token}));
      setNewlyCreatedKey(newKey.token);
      
      // Auto-complete the API key step
      //markStepComplete('api-key');
      
      toast("API Key created and added!", {
        description: `'${name}' has been successfully created and added to your Getting Started flow.`,
      });

      // Close the modal
      setIsCreateModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create API key';
      toast("Error creating API key", {
        description: errorMessage,
      });
    }
  };

  const handleGenerateExport = async () => {
    try {
      await exportDeleteService.exportMemories({
        user_id: formData.userId,
        project_id: formData.projectId,
        format: 'json'
      });
      toast("Export generated and downloaded successfully!");
      markStepComplete('compliance');
    } catch (error) {
      toast("Failed to generate export");
      console.error('Error generating export:', error);
    }
  };

  return (
    <PageLayout 
      title="Getting Started"
      description="Add your first memory in less than 5 minutes"
    >
      <div className="max-w-4xl mx-auto space-y-8">

        {/* Progress Bar */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Setup Progress</CardTitle>
                <CardDescription>
                  Complete all steps to unlock your full Recallio experience
                </CardDescription>
              </div>
              <Badge variant={allStepsComplete ? "default" : "secondary"} className="text-sm">
                {completedSteps.length} of {steps.length} complete
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={progressPercentage} className="h-3" />
            <div className="flex justify-between text-sm text-muted-foreground mt-2">
              <span>{Math.round(progressPercentage)}% complete</span>
              <span>{steps.length - completedSteps.length} steps remaining</span>
            </div>
          </CardContent>
        </Card>


        {/* Onboarding Steps */}
        <Accordion type="single" collapsible className="space-y-4" value={openAccordionItem} onValueChange={setOpenAccordionItem}>
          {/* Step 1: Install SDK */}
          <AccordionItem value="install" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-4 text-left">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  steps[0].isComplete 
                    ? 'bg-yellow-400' 
                    : 'bg-primary/10'
                }`}>
                  {steps[0].isComplete ? (
                    <Check className="h-4 w-4 text-black" />
                  ) : (
                    <span className="text-sm font-medium">1</span>
                  )}
                </div>
                <div>
                  <h3 className={`font-semibold flex items-center gap-2 ${
                    steps[0].isComplete ? 'line-through text-muted-foreground' : ''
                  }`}>
                    <Code className="h-4 w-4" />
                    {steps[0].title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{steps[0].description}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Python</Label>
                    <CodeBlock 
                      code={getCodeSnippet('install', 'python')} 
                      language="python"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Node.js</Label>
                    <CodeBlock 
                      code={getCodeSnippet('install', 'typescript')} 
                      language="typescript"
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => markStepComplete('install')}
                  disabled={steps[0].isComplete}
                  className="w-full"
                >
                  {steps[0].isComplete ? 'Completed' : 'Mark as Complete'}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Step 2: Generate API Key */}
          <AccordionItem value="api-key" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-4 text-left">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  steps[1].isComplete 
                    ? 'bg-yellow-400' 
                    : 'bg-primary/10'
                }`}>
                  {steps[1].isComplete ? (
                    <Check className="h-4 w-4 text-black" />
                  ) : (
                    <span className="text-sm font-medium">2</span>
                  )}
                </div>
                <div>
                  <h3 className={`font-semibold flex items-center gap-2 ${
                    steps[1].isComplete ? 'line-through text-muted-foreground' : ''
                  }`}>
                    <Key className="h-4 w-4" />
                    {steps[1].title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{steps[1].description}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="apiKey">API Key</Label>
                    <Input
                      id="apiKey"
                      placeholder="Your API key will appear here"
                      value={formData.apiKey}
                      readOnly={true}
                      className="font-mono bg-muted"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="writer">Writer</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Python</Label>
                    <CodeBlock 
                      code={getCodeSnippet('auth', 'python')} 
                      language="python"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Node.js</Label>
                    <CodeBlock 
                      code={getCodeSnippet('auth', 'typescript')} 
                      language="typescript"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={() => setIsCreateModalOpen(true)}
                    variant="outline" 
                    className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black border-yellow-400"
                  >
                    Generate API Key
                  </Button>
                  {formData.apiKey && (
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(formData.apiKey);
                        toast("API Key copied", {
                          description: "The API key has been copied to your clipboard.",
                        });
                      }}
                      variant="outline"
                      className="px-3"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                  <Button 
                    onClick={() => markStepComplete('api-key')}
                    disabled={steps[1].isComplete || !formData.apiKey}
                    className="flex-1"
                  >
                    {steps[1].isComplete ? 'Completed' : 'Save & Continue'}
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Step 3: Write Memory */}
          <AccordionItem value="write-memory" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-4 text-left">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  steps[2].isComplete 
                    ? 'bg-yellow-400' 
                    : 'bg-primary/10'
                }`}>
                  {steps[2].isComplete ? (
                    <Check className="h-4 w-4 text-black" />
                  ) : (
                    <span className="text-sm font-medium">3</span>
                  )}
                </div>
                <div>
                  <h3 className={`font-semibold flex items-center gap-2 ${
                    steps[2].isComplete ? 'line-through text-muted-foreground' : ''
                  }`}>
                    <Database className="h-4 w-4" />
                    {steps[2].title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{steps[2].description}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="userId">User ID</Label>
                    <Input
                      id="userId"
                      value={formData.userId}
                      onChange={(e) => setFormData({...formData, userId: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="projectId">Project ID</Label>
                    <Input
                      id="projectId"
                      value={formData.projectId}
                      onChange={(e) => setFormData({...formData, projectId: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="consentFlag">Consent Flag</Label>
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch
                        id="consentFlag"
                        checked={formData.consentFlag}
                        onCheckedChange={(checked) => setFormData({...formData, consentFlag: checked})}
                      />
                      <Label htmlFor="consentFlag">{formData.consentFlag ? 'Granted' : 'Denied'}</Label>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Python</Label>
                    <CodeBlock 
                      code={getCodeSnippet('write', 'python')} 
                      language="python"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Node.js</Label>
                    <CodeBlock 
                      code={getCodeSnippet('write', 'typescript')} 
                      language="typescript"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={() => markStepComplete('write-memory')}
                  disabled={steps[2].isComplete}
                  className="w-full"
                >
                  {steps[2].isComplete ? 'Completed' : 'Execute & Mark Complete'}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Step 4: Recall Memory */}
          <AccordionItem value="recall-memory" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-4 text-left">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  steps[3].isComplete 
                    ? 'bg-yellow-400' 
                    : 'bg-primary/10'
                }`}>
                  {steps[3].isComplete ? (
                    <Check className="h-4 w-4 text-black" />
                  ) : (
                    <span className="text-sm font-medium">4</span>
                  )}
                </div>
                <div>
                  <h3 className={`font-semibold flex items-center gap-2 ${
                    steps[3].isComplete ? 'line-through text-muted-foreground' : ''
                  }`}>
                    <Search className="h-4 w-4" />
                    {steps[3].title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{steps[3].description}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="query">Query</Label>
                    <Input
                      id="query"
                      value={formData.query}
                      onChange={(e) => setFormData({...formData, query: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="scope">Scope</Label>
                    <Select value={formData.scope} onValueChange={(value) => setFormData({...formData, scope: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="project">Project</SelectItem>
                        <SelectItem value="global">Global</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="summarized"
                        checked={formData.summarized}
                        onCheckedChange={(checked) => setFormData({...formData, summarized: checked})}
                      />
                      <Label htmlFor="summarized">Summarized</Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type">Type</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="facts">Facts</SelectItem>
                        <SelectItem value="raw">Raw</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="similarityThreshold">
                      Similarity Threshold: {formData.similarityThreshold[0]}
                    </Label>
                    <Slider
                      id="similarityThreshold"
                      min={0}
                      max={1}
                      step={0.1}
                      value={formData.similarityThreshold}
                      onValueChange={(value) => setFormData({...formData, similarityThreshold: value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="resultLimit">
                      Result Limit: {formData.resultLimit[0]}
                    </Label>
                    <Slider
                      id="resultLimit"
                      min={1}
                      max={100}
                      step={1}
                      value={formData.resultLimit}
                      onValueChange={(value) => setFormData({...formData, resultLimit: value})}
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Python</Label>
                    <CodeBlock 
                      code={getCodeSnippet('recall', 'python')} 
                      language="python"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Node.js</Label>
                    <CodeBlock 
                      code={getCodeSnippet('recall', 'typescript')} 
                      language="typescript"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={() => markStepComplete('recall-memory')}
                  disabled={steps[3].isComplete}
                  className="w-full"
                >
                  {steps[3].isComplete ? 'Completed' : 'Execute & Mark Complete'}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Step 5: Compliance */}
          <AccordionItem value="compliance" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-4 text-left">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  steps[4].isComplete 
                    ? 'bg-yellow-400' 
                    : 'bg-primary/10'
                }`}>
                  {steps[4].isComplete ? (
                    <Check className="h-4 w-4 text-black" />
                  ) : (
                    <span className="text-sm font-medium">5</span>
                  )}
                </div>
                <div>
                  <h3 className={`font-semibold flex items-center gap-2 ${
                    steps[4].isComplete ? 'line-through text-muted-foreground' : ''
                  }`}>
                    <Shield className="h-4 w-4" />
                    {steps[4].title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{steps[4].description}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="exportType">Export Type</Label>
                    <Select value={formData.exportType} onValueChange={(value) => setFormData({...formData, exportType: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fact">Fact</SelectItem>
                        <SelectItem value="raw">Raw</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="exportFormat">Export Format</Label>
                    <Select value={formData.exportFormat} onValueChange={(value) => setFormData({...formData, exportFormat: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="json">JSON</SelectItem>
                        <SelectItem value="csv">CSV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deleteScope">Delete Scope</Label>
                    <Select value={formData.deleteScope} onValueChange={(value) => setFormData({...formData, deleteScope: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="project">Project</SelectItem>
                        <SelectItem value="tag">Tag</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Python</Label>
                    <CodeBlock 
                      code={getCodeSnippet('compliance', 'python')} 
                      language="python"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Node.js</Label>
                    <CodeBlock 
                      code={getCodeSnippet('compliance', 'typescript')} 
                      language="typescript"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleGenerateExport}
                    variant="outline" 
                    className="flex-1"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Generate Export
                  </Button>
                  <Button 
                    onClick={() => markStepComplete('compliance')}
                    disabled={steps[4].isComplete}
                    className="flex-1"
                  >
                    {steps[4].isComplete ? 'Completed' : 'Mark Complete'}
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Footer */}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Need help? Check out our{' '}
                <Link to="/api-docs" className="text-primary hover:underline">
                  API Documentation
                </Link>{' '}
                or visit our{' '}
                <Link to="/help" className="text-primary hover:underline">
                  Help Center
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <CreateKeyModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setNewlyCreatedKey(null);
        }}
        onCreateKey={handleCreateKey}
        newlyCreatedKey={newlyCreatedKey}
      />
    </PageLayout>
  );
};

export default GettingStarted;